import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import {
  GraduationCap, Users, Award, Building2, Factory, HardHat,
  ArrowRight, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import './JoinEcosystem.css';

const site = 'https://your-platform.com';

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function JoinEcosystem() {
  const [selectedRole, setSelectedRole] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const roles = [
    {
      icon: 'GraduationCap',
      title: 'Graduate',
      description: 'Start your career with a verified competency passport.'
    },
    {
      icon: 'HardHat',
      title: 'Surveyor',
      description: 'Showcase your certifications and project history.'
    },
    {
      icon: 'Users',
      title: 'Mentor',
      description: 'Guide the next generation of professionals.'
    },
    {
      icon: 'Building2',
      title: 'Inspection Company',
      description: 'Manage your workforce and client relationships.'
    },
    {
      icon: 'Factory',
      title: 'EPC Contractor',
      description: 'Access verified talent for your projects.'
    },
    {
      icon: 'Award',
      title: 'Manufacturer',
      description: 'Ensure quality assurance across your supply chain.'
    }
  ];

  const iconMap = {
    GraduationCap, Users, Award, Building2, Factory, HardHat
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (honeypot) return;
    const form = e.currentTarget;
    const data = new FormData(form);

    const name = data.get('name')?.trim() || '';
    const email = data.get('email')?.trim() || '';
    const organisation = data.get('organisation')?.trim() || '';
    const role = selectedRole;
    const message = data.get('message')?.trim() || 'New registration interest submission';

    if (!email || !name || !role) {
      setErrorMsg('Please fill in all required fields and select your role.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setStatus('success');
      form.reset();
      setSelectedRole('');
    } catch {
      setErrorMsg('Something went wrong. Please try again.');
      setStatus('error');
    }
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${site}/join-ecosystem#webpage`,
    name: 'Register | Join the Qualion Ecosystem',
    url: `${site}/join-ecosystem`,
    isPartOf: { '@id': `${site}/#website` },
    about: { '@id': `${site}/#organization` },
  };

  return (
    <>
      <Helmet>
        <title>Register | Join the Qualion Ecosystem</title>
        <meta name="description" content="Join the Qualion ecosystem — for graduates, surveyors, mentors, inspection companies, EPC contractors, and manufacturers. Be part of the future of competency and assurance." />
        <link rel="canonical" href={`${site}/join-ecosystem`} />
        <meta property="og:title" content="Register | Join the Qualion Ecosystem" />
        <meta property="og:description" content="Be part of the future of competency and assurance — register your interest today." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${site}/join-ecosystem`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main>
        {/* ── HERO ── */}
        <section className="join-hero-section">
          <div className="join-hero-top-line" />
          <div className="join-hero-grid" />
          <div className="container join-hero-container">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="join-hero-content"
            >
              <div className="join-hero-eyebrow">
                <div className="join-hero-line" />
                <span>Register</span>
              </div>
              <h1 className="join-hero-title">Join the Qualion Ecosystem</h1>
              <p className="join-hero-subtitle">Be part of the future of competency and assurance — register your interest today.</p>
            </motion.div>
          </div>
        </section>

        {/* ── ROLE SELECTOR + FORM ── */}
        <section className="join-form-section">
          <div className="container">
            <div className="join-form-grid">
              {/* Role cards */}
              <div className="join-roles">
                <FadeUp>
                  <h2 className="join-roles-title">Who Are You?</h2>
                  <p className="join-roles-subtitle">Select the profile that best describes you — this helps us tailor your onboarding.</p>
                </FadeUp>
                <div className="join-roles-grid">
                  {roles.map((role, i) => {
                    const Icon = iconMap[role.icon] || Users;
                    const isSelected = selectedRole === role.title;
                    return (
                      <FadeUp key={i} delay={i * 0.06}>
                        <motion.button
                          whileHover={{ y: -2 }}
                          transition={{ duration: 0.15 }}
                          onClick={() => setSelectedRole(role.title)}
                          className={`join-role-btn ${isSelected ? 'active' : ''}`}
                        >
                          <div className="join-role-header">
                            <div className={`join-role-icon ${isSelected ? 'active' : ''}`}>
                              <Icon size={15} />
                            </div>
                            {isSelected && (
                              <div className="join-role-check" />
                            )}
                          </div>
                          <h3 className={`join-role-title ${isSelected ? 'active' : ''}`}>
                            {role.title}
                          </h3>
                          <p className={`join-role-description ${isSelected ? 'active' : ''}`}>
                            {role.description}
                          </p>
                        </motion.button>
                      </FadeUp>
                    );
                  })}
                </div>
              </div>

              {/* Form */}
              <FadeUp delay={0.1}>
                <div className="join-form-card">
                  <h2 id="contactform" className="join-form-title">Tell Us About Yourself</h2>
                  <p className="join-form-subtitle">Select your role and provide your details to begin your journey with Qualion.</p>

                  {status === 'success' ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="join-success"
                    >
                      <div className="join-success-icon">
                        <CheckCircle size={32} />
                      </div>
                      <h3 className="join-success-title">Registration Received</h3>
                      <p className="join-success-text">
                        Thank you for your interest. Our team will be in touch shortly.
                      </p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} noValidate>
                      {/* Honeypot */}
                      <input
                        type="text"
                        name="_gotcha"
                        tabIndex={-1}
                        autoComplete="off"
                        style={{ position: 'absolute', left: '-9999px' }}
                        aria-hidden="true"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />

                      <div className="join-form-fields">
                        <div className="join-form-row">
                          <div className="join-form-group">
                            <label htmlFor="reg-name" className="join-form-label">
                              Full Name <span className="join-form-required">*</span>
                            </label>
                            <input
                              id="reg-name"
                              name="name"
                              type="text"
                              required
                              placeholder="Your full name"
                              className="join-form-input"
                            />
                          </div>
                          <div className="join-form-group">
                            <label htmlFor="reg-email" className="join-form-label">
                              Email Address <span className="join-form-required">*</span>
                            </label>
                            <input
                              id="reg-email"
                              name="email"
                              type="email"
                              required
                              placeholder="your@email.com"
                              className="join-form-input"
                            />
                          </div>
                        </div>

                        <div className="join-form-group">
                          <label htmlFor="reg-org" className="join-form-label">
                            Organisation / Company
                          </label>
                          <input
                            id="reg-org"
                            name="organisation"
                            type="text"
                            placeholder="Your organisation (if applicable)"
                            className="join-form-input"
                          />
                        </div>

                        <div className="join-form-group">
                          <label className="join-form-label">
                            Your Role <span className="join-form-required">*</span>
                          </label>
                          <div className={`join-form-role-display ${selectedRole ? 'active' : ''}`}>
                            {selectedRole || 'Select your role from the cards on the left'}
                          </div>
                        </div>

                        <div className="join-form-group">
                          <label htmlFor="reg-message" className="join-form-label">
                            Tell Us More (optional)
                          </label>
                          <textarea
                            id="reg-message"
                            name="message"
                            rows={4}
                            placeholder="Share your background, goals, or any specific questions..."
                            className="join-form-textarea"
                          />
                        </div>

                        {status === 'error' && (
                          <div className="join-error">
                            <AlertCircle size={16} className="join-error-icon" />
                            <p className="join-error-text">{errorMsg}</p>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={status === 'loading'}
                          className="join-submit-btn"
                        >
                          {status === 'loading' ? (
                            <>
                              <Loader2 size={16} className="join-spinner" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              Register Interest
                              <ArrowRight size={16} />
                            </>
                          )}
                        </button>

                        <p className="join-terms">
                          By submitting, you agree to be contacted regarding your registration.
                        </p>
                      </div>
                    </form>
                  )}
                </div>
              </FadeUp>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}